import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useMoney } from "@units/useMoney";
import { fmtPct } from "@lib/format";
import { ChartFrame, useChartTheme } from "@components/chart";
import { InlineRowGrid, type InlineColumn } from "@components/input";
import { CatalogPicker } from "@components/catalog";
import type { MessageKey } from "@i18n/messages";
import type { PadRollupOut, PadSharedCostLine, AllocationMethod } from "@api/types";
import { useGetPadSharedCostsQuery, useCreatePadSharedCostMutation } from "@api/pApi";

/** Allocation methods (§12) — analogous to AFE accrual, one label each. */
const ALLOC_METHODS: { id: AllocationMethod; key: MessageKey }[] = [
  { id: "equal", key: "pad.alloc.equal" },
  { id: "well_count", key: "pad.alloc.wellCount" },
  { id: "footage", key: "pad.alloc.footage" },
  { id: "rig_days", key: "pad.alloc.rigDays" },
  { id: "afe_weight", key: "pad.alloc.afeWeight" },
  { id: "manual_pct", key: "pad.alloc.manualPct" },
];

interface SharedDraft {
  cost_class_entry_key: string | null;
  description: string;
  vendor_entry_key: string | null;
  amount: string;
  allocation_method: AllocationMethod;
}

/**
 * Pad costs (§12) — the shared/pad cost lines with their allocation method +
 * server-computed per-well split, the per-well rollup (direct + allocated =
 * total), and a stacked bar of pad cost by well.
 */
export function PadCostSection({
  siteId,
  rollup,
}: {
  siteId: string;
  rollup: PadRollupOut;
}) {
  const { t, locale } = useLocale();
  const { money } = useMoney();
  const ccy = rollup.currency ?? null;
  // `per_well` can be absent on a partial/error body even when `rollup` is truthy.
  const perWell = rollup.per_well ?? [];

  const { data: shared } = useGetPadSharedCostsQuery(siteId, { skip: !siteId });
  const [createLine] = useCreatePadSharedCostMutation();
  const [expanded, setExpanded] = useState<string | null>(null);

  const lines = Array.isArray(shared?.lines) ? shared.lines : [];
  const allocMethodLabel = (m: AllocationMethod) =>
    t(ALLOC_METHODS.find((x) => x.id === m)?.key ?? "pad.alloc.equal");

  const columns: InlineColumn<PadSharedCostLine, SharedDraft>[] = [
    {
      key: "cost_class",
      header: t("pad.col.costClass"),
      render: (r) => r.cost_class_entry_key,
      input: (d, patch) => (
        <CatalogPicker
          catalogKey="cost_accounts"
          value={d.cost_class_entry_key}
          onChange={(code) => patch({ cost_class_entry_key: code })}
          placeholder={t("pad.col.costClass")}
          compact
          serverSearch
        />
      ),
    },
    {
      key: "description",
      header: t("pad.col.description"),
      render: (r) => r.description ?? "—",
      input: (d, patch) => (
        <input
          value={d.description}
          onChange={(e) => patch({ description: e.target.value })}
          placeholder={t("pad.col.description")}
        />
      ),
    },
    {
      key: "vendor",
      header: t("pad.col.vendor"),
      render: (r) => r.vendor_entry_key ?? "—",
      input: (d, patch) => (
        <CatalogPicker
          catalogKey="vendors"
          value={d.vendor_entry_key}
          onChange={(code) => patch({ vendor_entry_key: code })}
          placeholder={t("pad.col.vendor")}
          compact
        />
      ),
    },
    {
      key: "amount",
      header: t("pad.col.amount"),
      num: true,
      render: (r) => money(r.amount, ccy),
      input: (d, patch) => (
        <input
          inputMode="decimal"
          value={d.amount}
          onChange={(e) => patch({ amount: e.target.value })}
          placeholder="0"
        />
      ),
    },
    {
      key: "alloc",
      header: t("pad.col.allocMethod"),
      render: (r) => allocMethodLabel(r.allocation_method),
      input: (d, patch) => (
        <select
          value={d.allocation_method}
          onChange={(e) => patch({ allocation_method: e.target.value as AllocationMethod })}
          aria-label={t("pad.col.allocMethod")}
        >
          {ALLOC_METHODS.map((m) => (
            <option key={m.id} value={m.id}>
              {t(m.key)}
            </option>
          ))}
        </select>
      ),
    },
    {
      key: "split",
      header: t("pad.col.split"),
      render: (r) => (
        <button
          type="button"
          className="pad-split-toggle"
          onClick={() => setExpanded((cur) => (cur === r.id ? null : r.id))}
          aria-expanded={expanded === r.id}
        >
          {t("pad.viewSplit")}
        </button>
      ),
    },
  ];

  return (
    <>
      <h2 className="pad-section-head">{t("pad.costs")}</h2>

      {/* Shared / pad cost lines + allocation method + per-well split. */}
      <div className="card" style={{ marginTop: 12 }}>
        <h3>{t("pad.sharedLines")}</h3>
        <InlineRowGrid<PadSharedCostLine, SharedDraft>
          ariaLabel={t("pad.sharedLines")}
          columns={columns}
          rows={lines}
          rowKey={(r) => r.id}
          rowOwner={() => "p"}
          emptyLabel={t("pad.noSharedLines")}
          emptyDraft={() => ({
            cost_class_entry_key: null,
            description: "",
            vendor_entry_key: null,
            amount: "",
            allocation_method: "equal",
          })}
          toRow={(d) => {
            const amount = Number(d.amount);
            if (!d.cost_class_entry_key || !Number.isFinite(amount) || amount <= 0) return null;
            // Optimistic row id; the server (mock) re-issues + returns the split.
            return {
              id: `draft-${Date.now()}`,
              cost_class_entry_key: d.cost_class_entry_key,
              description: d.description || null,
              vendor_entry_key: d.vendor_entry_key,
              amount,
              allocation_method: d.allocation_method,
            };
          }}
          onCommit={(row) => {
            void createLine({
              siteId,
              body: {
                cost_class_entry_key: row.cost_class_entry_key,
                description: row.description,
                vendor_entry_key: row.vendor_entry_key,
                amount: row.amount,
                allocation_method: row.allocation_method,
              },
            });
          }}
        />

        {/* Expanded per-well split for the selected line. */}
        {expanded
          ? (() => {
              const line = lines.find((l) => l.id === expanded);
              if (!line?.split?.length) return null;
              return (
                <div className="pad-split" role="region" aria-label={t("pad.col.split")}>
                  <table>
                    <thead>
                      <tr>
                        <th>{t("pad.col.well")}</th>
                        <th className="num">{t("pad.col.share")}</th>
                        <th className="num">{t("pad.col.allocated")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {line.split.map((s) => (
                        <tr key={s.well_uwi}>
                          <td>{s.common_name}</td>
                          <td className="num tab-nums">{fmtPct(s.pct, locale, 1)}</td>
                          <td className="num tab-nums">{money(s.amount, ccy)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()
          : null}
      </div>

      {/* Per-well rollup: direct + allocated shared = total, % of pad. */}
      <div className="card" style={{ marginTop: 14 }}>
        <h3>{t("pad.perWellRollup")}</h3>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("pad.col.well")}</th>
                <th className="num">{t("pad.col.directCost")}</th>
                <th className="num">{t("pad.col.allocatedShared")}</th>
                <th className="num">{t("pad.col.total")}</th>
                <th className="num">{t("pad.col.pctOfPad")}</th>
              </tr>
            </thead>
            <tbody>
              {perWell.map((w) => (
                <tr key={w.well_uwi} data-owner="p">
                  <td>{w.common_name}</td>
                  <td className="num tab-nums">{money(w.direct_cost, ccy)}</td>
                  <td className="num tab-nums">{money(w.allocated_shared, ccy)}</td>
                  <td className="num tab-nums">{money(w.total_cost, ccy)}</td>
                  <td className="num tab-nums">{fmtPct(w.pct_of_pad, locale)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>{t("pad.col.padTotal")}</th>
                <td className="num tab-nums">
                  {money(
                    perWell.reduce((s, w) => s + w.direct_cost, 0),
                    ccy,
                  )}
                </td>
                <td className="num tab-nums">
                  {money(
                    perWell.reduce((s, w) => s + w.allocated_shared, 0),
                    ccy,
                  )}
                </td>
                <td className="num tab-nums">{money(rollup.pad_total_cost, ccy)}</td>
                <td className="num tab-nums">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <PadCostByWellChart rollup={rollup} />
      </div>
    </>
  );
}

/** Stacked bar: pad cost by well (direct + allocated shared). */
function PadCostByWellChart({ rollup }: { rollup: PadRollupOut }) {
  const { t } = useLocale();
  const theme = useChartTheme();
  const wells = useMemo(() => rollup.per_well ?? [], [rollup]);
  const x = useMemo(() => wells.map((w) => w.common_name), [wells]);

  return (
    <ChartFrame
      title={t("pad.chart.costByWell")}
      subtitle={rollup.currency ?? undefined}
      ariaLabel={t("pad.chart.costByWell")}
      xTitle={t("pad.col.well")}
      yTitle={rollup.currency ?? t("pad.col.total")}
      data={[
        {
          type: "bar",
          name: t("pad.col.directCost"),
          x,
          y: wells.map((w) => w.direct_cost),
          marker: { color: theme.oxboZ },
        },
        {
          type: "bar",
          name: t("pad.col.allocatedShared"),
          x,
          y: wells.map((w) => w.allocated_shared),
          marker: { color: theme.warn },
        },
      ]}
      layout={{ barmode: "stack" }}
    />
  );
}
